var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { localized, msg } from "@lit/localize";
import { DataFrame } from "../../model/DataFrame";
import { getDataFrameFromPois } from "../../utils/poi";
import { CommonPlaceholders, CommonMessages } from "../../localization/helpers.js";
import "../icon/Icon.js";
let TableComponent = class TableComponent extends LitElement {
    constructor() {
        super(...arguments);
        this.listPois = [];
        this.selectedPoi = null;
        this.listNetworkFields = [];
        this.table = null;
        this.searchQuery = "";
        this.sortColumn = null;
        this.sortDirection = "asc";
        this.currentPage = 1;
        this.selectedRows = new Set();
        this.rowsPerPage = 20;
    }
    // --------------------------
    // Table Generation
    // --------------------------
    generateTable() {
        if (!this.selectedNetwork || !this.listPois?.length) {
            this.table = null;
            return;
        }
        // Toujours charger toutes les données
        this.table = getDataFrameFromPois(this.listPois, this.listNetworkFields);
    }
    update(changedProperties) {
        if ((changedProperties.has("selectedNetwork") && this.selectedNetwork) ||
            (changedProperties.has("listPois") && this.listPois.length > 0) ||
            (changedProperties.has("listNetworkFields") && this.listNetworkFields.length > 0)) {
            this.currentPage = 1;
            this.generateTable();
        }
        super.update(changedProperties);
    }
    // --------------------------
    // Handlers
    // --------------------------
    handleSearch(event) {
        const input = event.target;
        this.searchQuery = input.value.toLowerCase();
        this.currentPage = 1;
    }
    handleSort(column) {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
        }
        else {
            this.sortColumn = column;
            this.sortDirection = "asc";
        }
    }
    // Ajouter un événement personnalisé lors de la sélection d'une ligne
    toggleRowSelection(index) {
        const globalIndex = (this.currentPage - 1) * this.rowsPerPage + index;
        const selectedRow = this.sortedRows[globalIndex];
        if (!selectedRow)
            return;
        if (this.selectedRows.has(globalIndex)) {
            // Si la ligne est déjà sélectionnée, la désélectionner
            this.selectedRows.clear();
            this.selectedPoi = null;
            // Émettre un événement personnalisé pour indiquer qu'aucun POI n'est sélectionné
            this.dispatchEvent(new CustomEvent('poi-selected', {
                detail: { selectedPoi: null },
                bubbles: true,
                composed: true
            }));
        }
        else {
            // Sinon, sélectionner la ligne
            this.selectedRows.clear();
            this.selectedRows.add(globalIndex);
            if (selectedRow.poi_id) {
                this.selectedPoi = this.listPois.find(poi => poi.poi_id === selectedRow.poi_id) || null;
                // Émettre un événement personnalisé pour indiquer le POI sélectionné
                this.dispatchEvent(new CustomEvent('poi-selected', {
                    detail: { selectedPoi: this.selectedPoi },
                    bubbles: true,
                    composed: true
                }));
            }
        }
    }
    // --------------------------
    // Computed Data
    // --------------------------
    get allRows() {
        return this.table?.rows ?? [];
    }
    get filteredRows() {
        if (!this.searchQuery)
            return this.allRows;
        return this.allRows.filter(row => Object.values(row).some(val => String(val).toLowerCase().includes(this.searchQuery)));
    }
    get sortedRows() {
        const rows = [...this.filteredRows];
        if (this.sortColumn) {
            rows.sort((a, b) => {
                const valA = a[this.sortColumn];
                const valB = b[this.sortColumn];
                return this.sortDirection === "asc"
                    ? valA < valB
                        ? -1
                        : valA > valB
                            ? 1
                            : 0
                    : valA < valB
                        ? 1
                        : valA > valB
                            ? -1
                            : 0;
            });
        }
        return rows;
    }
    get paginatedRows() {
        const start = (this.currentPage - 1) * this.rowsPerPage;
        const end = start + this.rowsPerPage;
        return this.sortedRows.slice(start, end);
    }
    get totalPages() {
        return Math.max(1, Math.ceil(this.sortedRows.length / this.rowsPerPage));
    }
    goToPage(page) {
        if (page >= 1 && page <= this.totalPages) {
            this.currentPage = page;
        }
    }
    // --------------------------
    // Render
    // --------------------------
    render() {
        if (!this.selectedNetwork)
            return html `<div class="empty">${CommonMessages.noNetwork()}</div>`;
        if (!this.table)
            return html `<div class="empty">${msg('No data found for this network', { id: 'table.error.noDataForNetwork', desc: 'No data for network error' })}</div>`;
        if (!this.allRows.length)
            return html `<div class="empty">${CommonMessages.noPoi()}</div>`;
        return html `
      <div class="header">
        <div class="row-count">${msg('Number of rows:', { id: 'table.rowCount', desc: 'Row count label' })} ${this.sortedRows.length}</div>
        <div class="search-container">
          <input
            class="search-bar"
            type="text"
            placeholder=${CommonPlaceholders.search()}
            @input=${this.handleSearch}
          />
          <div class="pagination">
            <button
              ?disabled=${this.currentPage === 1}
              @click=${() => this.goToPage(this.currentPage - 1)}
            >
              <basic-icon name="back" size="16" .label=${msg('Previous', { id: 'table.pagination.previous', desc: 'Previous page button' })}></basic-icon>
            </button>
            <span class="page-indicator">
              ${msg('Page', { id: 'table.pagination.page', desc: 'Page label' })} ${this.currentPage} / ${this.totalPages}
            </span>
            <button
              ?disabled=${this.currentPage === this.totalPages}
              @click=${() => this.goToPage(this.currentPage + 1)}
            >
              <basic-icon name="next" size="16" .label=${msg('Next', { id: 'table.pagination.next', desc: 'Next page button' })}></basic-icon>
            </button>
          </div>
        </div>
      </div>

      <div class="table-container">
        <table>
          <thead>
            <tr>
              ${this.table.columns.map(col => html `
                  <th @click=${() => this.handleSort(col.name)}>
                    ${col.alias}
                    ${this.sortColumn === col.name
            ? this.sortDirection === "asc"
                ? "▲"
                : "▼"
            : ""}
                  </th>
                `)}
            </tr>
          </thead>
          <tbody>
            ${this.paginatedRows.map((row, index) => {
            const globalIndex = (this.currentPage - 1) * this.rowsPerPage + index;
            return html `
                <tr
                  class=${this.selectedRows.has(globalIndex) ? "selected" : ""}
                  @click=${() => this.toggleRowSelection(index)}
                >
                  ${this.table.columns.map(col => html `<td>${row[col.name] ?? "-"}</td>`)}
                </tr>
              `;
        })}
          </tbody>
        </table>
      </div>
    `;
    }
};
// --------------------------
// Styles
// --------------------------
TableComponent.styles = css `
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      background: #fff;
      border: 1px solid #ddd;
      border-radius: 12px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
      padding: 15px;
      box-sizing: border-box;
      font-family: inherit;
      color: #333;
      margin-top: 15px;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;
    }

    .row-count {
      font-size: 13px;
      color: #666;
    }

    .search-container {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .search-bar {
      padding: 6px 10px;
      font-size: 13px;
      border: 1px solid #888;
      border-radius: 8px;
      width: 200px;
      background: #f9f9f9;
      color: #333;
    }

    .search-bar::placeholder {
      color: #999;
    }

    .pagination {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .pagination button {
      padding: 4px 8px;
      border: 1px solid #888;
      border-radius: 4px;
      background: #f9f9f9;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .pagination span.page-indicator {
      font-size: 13px;
    }

    .table-container {
      flex: 1;
      overflow: auto;
    }

    .table-container::-webkit-scrollbar {
      height: 8px;
      background: #f0f0f0;
    }
    .table-container::-webkit-scrollbar-thumb {
      background: #ccc;
      border-radius: 4px;
    }
    .table-container::-webkit-scrollbar-thumb:hover {
      background: #aaa;
    }
    .table-container {
      scrollbar-width: thin;
      scrollbar-color: #ccc #f0f0f0;
    }

    table {
      width: max-content;
      min-width: 100%;
      border-collapse: collapse;
      table-layout: auto;
    }

    th,
    td {
      padding: 8px;
      font-size: 13px;
      text-align: left;
      border: 1px solid #ccc;
      max-width: 150px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    th {
      background: #f2f2f2;
      font-weight: bold;
      cursor: pointer;
      user-select: none;
    }

    tr:nth-child(even) {
      background: #fafafa;
    }

    tr:hover {
      background: #e6f7ff;
    }

    tr.selected {
      background: #cce5ff;
    }

    .empty {
      padding: 20px;
      text-align: center;
      color: #777;
    }
  `;
__decorate([
    property({ type: Object })
], TableComponent.prototype, "selectedNetwork", void 0);
__decorate([
    property({ type: Array })
], TableComponent.prototype, "listPois", void 0);
__decorate([
    property({ type: Object })
], TableComponent.prototype, "selectedPoi", void 0);
__decorate([
    property({ type: Array })
], TableComponent.prototype, "listNetworkFields", void 0);
__decorate([
    state()
], TableComponent.prototype, "table", void 0);
__decorate([
    state()
], TableComponent.prototype, "searchQuery", void 0);
__decorate([
    state()
], TableComponent.prototype, "sortColumn", void 0);
__decorate([
    state()
], TableComponent.prototype, "sortDirection", void 0);
__decorate([
    state()
], TableComponent.prototype, "currentPage", void 0);
__decorate([
    state()
], TableComponent.prototype, "selectedRows", void 0);
TableComponent = __decorate([
    localized(),
    customElement("table-component")
], TableComponent);
export { TableComponent };
//# sourceMappingURL=TableComponent.js.map